/**
 * Landmarks → Features. Pure.
 *
 * Every distance is normalized by the scale reference dist(0, 9) — the hand's
 * own geometry — which makes the measures invariant to how far the hand sits
 * from the camera (gesture-vocabulary.md, "Landmark reference"). Nothing here
 * knows about a camera; a fixture replay produces identical output.
 *
 * This is the seam Phase 1 vision plugs into: a live HandLandmarker frame maps
 * to `Hand` and lands here unchanged. Nothing downstream needs to know which.
 */

import type { Features, Hand } from './types';
import { NEUTRAL_FEATURES } from './types';

const dist2 = (a: readonly number[], b: readonly number[]): number => Math.hypot(a[0] - b[0], a[1] - b[1]);

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Normalize a raw ratio into 0..1 across a calibrated range. */
export const norm = (v: number, lo: number, hi: number): number =>
  hi === lo ? 0 : clamp01((v - lo) / (hi - lo));

export interface Ranges {
  aperture: [number, number];
  spread: [number, number];
  height: [number, number];
}

export const DEFAULT_RANGES: Ranges = {
  aperture: [0.15, 1.1],
  spread: [0.4, 1.6],
  height: [0.15, 0.85],
};

/** The pre-normalization ratios — what calibration samples and ranges scale. */
export interface RawMeasures {
  aperture: number;
  spread: number;
  height: number;
}

/**
 * The raw geometry, before any range is applied. ONE implementation:
 * featuresFromHand normalizes exactly these values, so a calibration fitted
 * from rawMeasures is by construction in the units the normalizer consumes.
 */
export function rawMeasures(hand: Hand | undefined): RawMeasures | null {
  if (!hand || hand.points.length < 21) return null;
  const p = hand.points;
  const scale = dist2(p[0], p[9]) || 1e-6;
  return {
    aperture: dist2(p[4], p[8]) / scale,
    spread: dist2(p[8], p[20]) / scale,
    // height: y grows downward in MediaPipe, so invert before normalizing
    height: 1 - p[9][1],
  };
}

export function featuresFromHand(hand: Hand | undefined, ranges: Ranges = DEFAULT_RANGES): Features {
  const raw = rawMeasures(hand);
  if (!hand || !raw) return { ...NEUTRAL_FEATURES };
  const p = hand.points;
  const roll = Math.atan2(p[17][1] - p[5][1], p[17][0] - p[5][0]);
  const angle = Math.atan2(p[9][1] - 0.5, p[9][0] - 0.5);
  return {
    aperture: norm(raw.aperture, ranges.aperture[0], ranges.aperture[1]),
    spread: norm(raw.spread, ranges.spread[0], ranges.spread[1]),
    height: norm(raw.height, ranges.height[0], ranges.height[1]),
    // The doc's roll row: ±40° is the full working range, hard-clamped at ±60°
    // because the 5→17 vector is ambiguous past there. The old ±126°
    // normalization ran straight through the ambiguity zone and needed three
    // times the wrist travel for full resonance.
    roll: clamp01(clampRad(roll, DEG_60) / (2 * DEG_40) + 0.5),
    angle,
    fistPose: isFist(hand),
    present: true,
    confident: hand.score >= 0.5,
  };
}

const DEG_40 = (40 * Math.PI) / 180;
const DEG_60 = (60 * Math.PI) / 180;
const clampRad = (v: number, lim: number): number => (v < -lim ? -lim : v > lim ? lim : v);

/** all fingertips within 0.6 of the palm centroid — the fist test. */
export function isFist(hand: Hand | undefined): boolean {
  if (!hand || hand.points.length < 21) return false;
  const p = hand.points;
  const scale = dist2(p[0], p[9]) || 1e-6;
  const palm = [(p[0][0] + p[9][0]) / 2, (p[0][1] + p[9][1]) / 2];
  return [8, 12, 16, 20].every((i) => dist2(p[i], palm) / scale < 0.6);
}
